import React, { useState, useEffect, useContext, useRef } from "react";
import { AppContext } from "../AppContext";
import { PLATAFORMAS, PlataformaId } from "../config/api";
import { getInfo } from "../services/infoService";
import { useLocation, useNavigate } from "react-router";
import { CircularProgress } from "@mui/material";

interface Seccion {
  id_seccion: string;
  destacada: Number;
  number: number;
  seccion: string;
}

const Aside: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useContext(AppContext);

  const hash = localStorage.getItem("hash") || "";

  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [loading, setLoading] = useState(true);
  const [loads, setLoads] = useState(0);

  const contextSecciones = context?.data?.secciones;
  const activeSeccionId = context?.activeSeccionId;
  const setActiveSeccionId = context?.setActiveSeccionId;
  const isWeb = context?.plataforma === PLATAFORMAS.WEB;

  const isFetchingSidebar = useRef(false);

  useEffect(() => {
    const verificarYFezchar = async () => {
      const tieneSecciones = contextSecciones && contextSecciones.length > 0;

      if (!tieneSecciones && !isFetchingSidebar.current) {
        console.log("contextSecciones.length:", contextSecciones?.length, "loads:", loads, "isFetchingSidebar.current:", isFetchingSidebar.current);
        if (hash && contextSecciones?.length === 0 && loads < 3) {
          console.log("Cargando secciones en Aside...");
          try {
            isFetchingSidebar.current = true;
            setLoading(true);
            await getInfo(context, hash, context?.plataforma);
          } catch (error) {
            console.error("Error recuperando secciones en Aside:", error);
          } finally {
            isFetchingSidebar.current = false;
            setLoads((prev) => prev + 1);
          }
        } else {
          setLoading(false);
        }
      }
    };

    verificarYFezchar();
  }, [contextSecciones, context, hash]);

  useEffect(() => {
    const info: Seccion[] = contextSecciones || [];

    if (info.length === 0) {
      setSecciones([]);
      if (!isFetchingSidebar.current) setLoading(false);
      return;
    }

    setSecciones(info);

    const hasActiveSection = info.some(
      (seccion) => String(seccion.id_seccion) === String(activeSeccionId)
    );

    if (!hasActiveSection && setActiveSeccionId) {
      setActiveSeccionId(info[0].id_seccion);
    }

    setLoading(false);
  }, [contextSecciones, activeSeccionId, setActiveSeccionId]);

  const closeMobileAside = () => {
    context?.setMobileAsideOpen(false);
  };

  const handlePlatformChange = async (plataforma: PlataformaId) => {
    if (!context || context.plataforma === plataforma) return;

    context.setPlataforma(plataforma);
    context.setActiveSeccionId(null);
    setLoading(true);

    // if (hash) {
      await getInfo(context, hash, plataforma);

      console.log("Secciones actualizadas después de cambiar la plataforma:", context.data?.secciones);
    // }

    setLoading(false);

    if (location.pathname.includes("admin")) {
      return;
    }

    navigate("/home/" + hash);
    closeMobileAside();
  };

  const platformButtonClass = (active: boolean) =>
    `h-11 flex items-center justify-center rounded-3xl transition-all duration-200 ${active
      ? "bg-primary text-white shadow-sm"
      : "text-on-surface-variant hover:bg-surface-container hover:text-primary"
    }`;

  const renderPlatformToggle = () => (
    <div className="px-2 mb-6">
      <div className="grid grid-cols-2 gap-2 rounded-3xl border-2 border-outline-variant bg-surface-container-lowest p-1">
        <button
          type="button"
          className={platformButtonClass(isWeb)}
          onClick={() => handlePlatformChange(PLATAFORMAS.WEB)}
          aria-label="Tutoriales Web"
          title="Tutoriales Web"
        >
          <span className="material-symbols-outlined text-xl">desktop_windows</span>
        </button>
        <button
          type="button"
          className={platformButtonClass(!isWeb)}
          onClick={() => handlePlatformChange(PLATAFORMAS.MOVIL)}
          aria-label="Tutoriales App"
          title="Tutoriales App"
        >
          <span className="material-symbols-outlined text-xl">smartphone</span>
        </button>
      </div>
    </div>
  );

  const renderSkeleton = () => (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="h-10 w-full rounded-3xl bg-gray-300 dark:bg-gray-700 animate-pulse" />
      ))}
    </div>
  );

  return (
    <>
      <aside className="h-screen w-64 fixed left-0 top-0 bg-surface-container-low border-r border-outline-variant pt-24 flex-col px-4 z-40 hidden md:flex">
        {renderPlatformToggle()}

        {!loading && secciones.length > 0 ? (
          <>
            <div className="border-t mb-3 border-outline-variant" />

            <button
              onClick={() => {
                context?.setActiveView('rapidos');
                context?.setActiveSeccionId(null);
                navigate("/home/" + hash);
              }}
              className={`flex items-center gap-3 px-3 py-3 rounded-3xl text-left transition-all duration-200 ${context?.activeView === 'rapidos'
                ? "bg-surface-container-highest text-primary font-bold shadow-sm"
                : "text-on-surface-variant hover:text-primary-container hover:bg-surface-container/50 hover:translate-x-1"
                }`}
            >
              <span className="material-symbols-outlined text-xl">bolt</span>
              <span className="font-label-md text-label-md line-clamp-2">
                Vínculos Rápidos
              </span>
            </button>

            <button
              onClick={() => {
                context?.setActiveView('populares');
                context?.setActiveSeccionId(null);
                navigate("/home/" + hash);
              }}
              className={`flex items-center gap-3 px-3 py-3 rounded-3xl text-left transition-all duration-200 ${context?.activeView === 'populares'
                ? "bg-surface-container-highest text-primary font-bold shadow-sm"
                : "text-on-surface-variant hover:text-primary-container hover:bg-surface-container/50 hover:translate-x-1"
                }`}
            >
              <span className="material-symbols-outlined text-xl">trending_up</span>
              <span className="font-label-md text-label-md line-clamp-2">
                Vínculos Populares
              </span>
            </button>

            <div className="border-t border-outline-variant my-3" />

            <div className="px-2 mb-2">
              <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary block">
                Secciones
              </span>
            </div>
          </>
        ) : (
          <div className="flex justify-center items-center py-8">
            <div>
              <p className="text-on-surface-variant">
                No hay secciones disponibles
              </p>
            </div>
          </div>
        )}

        <nav className="flex-1 flex flex-col gap-1 overflow-y-auto pr-2 pb-4 ">
          {loading ? (
            renderSkeleton()
          ) : (
            secciones.map((seccion) => (
              <button
                key={seccion.id_seccion}
                onClick={() => {
                  context?.setActiveView('seccion');
                  context?.setActiveSeccionId(seccion.id_seccion);
                  navigate("/home/" + hash);
                }}
                className={`flex items-center gap-3 px-3 py-3 rounded-3xl text-left transition-all duration-200 ${context?.activeView === 'seccion' && activeSeccionId === seccion.id_seccion
                  ? "bg-surface-container-highest text-primary font-bold shadow-sm"
                  : "text-on-surface-variant hover:text-primary-container hover:bg-surface-container/50 hover:translate-x-1"
                  }`}
              >
                <span className="material-symbols-outlined text-xl" style={seccion.destacada === 1 ? { fontVariationSettings: "'FILL' 1" } : {}}>
                  {seccion.destacada === 1 ? "star" : "folder"}
                </span>
                <span className="font-label-md text-label-md line-clamp-2">
                  {seccion.seccion}
                </span>
              </button>
            ))
          )}
        </nav>

        {/* <div className="mt-auto mb-8 pt-8 border-t border-outline-variant">
          <a
            href="http://blog.colpegasus.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full bg-primary text-white py-3 px-4 font-label-md text-label-md hover:bg-primary-container transition-colors active:scale-95 duration-100 rounded-full flex items-center justify-center gap-2 no-underline"
          >
            <span className="material-symbols-outlined text-sm">open_in_new</span>
            Ver Documentación
          </a>
        </div> */}
      </aside>

      {/* Mobile Aside */}
      <div
        className={`fixed inset-x-0 bottom-0 top-16 z-40 md:hidden transition-opacity duration-300 ${context?.isMobileAsideOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!context?.isMobileAsideOpen}
      >
        <button
          type="button"
          aria-label="Cerrar menú lateral"
          className="absolute inset-0 bg-black/50"
          onClick={closeMobileAside}
        />
        <aside
          className={`absolute left-0 top-0 flex h-full w-[86vw] max-w-[22rem] flex-col overflow-hidden bg-surface-container-low border-r border-outline-variant px-4 py-5 pt-0 shadow-2xl transform transition-transform duration-300 ease-out ${context?.isMobileAsideOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div />
            <button
              type="button"
              className="material-symbols-outlined text-on-surface-variant hover:text-on-surface transition-colors p-2 rounded-full"
              onClick={closeMobileAside}
              title="Cerrar"
            >
              close
            </button>
          </div>

          {renderPlatformToggle()}

          <div className="border-t border-outline-variant" />

          <button
            onClick={() => {
              context?.setActiveView('rapidos');
              context?.setActiveSeccionId(null);
              navigate("/home/" + hash);
              closeMobileAside();
            }}
            className={`flex items-center gap-3 px-3 py-3 rounded-3xl text-left transition-all duration-200 ${context?.activeView === 'rapidos'
              ? "bg-surface-container-highest text-primary font-bold shadow-sm"
              : "text-on-surface-variant hover:text-primary-container hover:bg-surface-container/50 hover:translate-x-1"
              }`}
          >
            <span className="material-symbols-outlined text-xl">bolt</span>
            <span className="font-label-md text-label-md line-clamp-2">
              Vínculos Rápidos
            </span>
          </button>

          <button
            onClick={() => {
              context?.setActiveView('populares');
              context?.setActiveSeccionId(null);
              navigate("/home/" + hash);
              closeMobileAside();
            }}
            className={`flex items-center gap-3 px-3 py-3 rounded-3xl text-left transition-all duration-200 ${context?.activeView === 'populares'
              ? "bg-surface-container-highest text-primary font-bold shadow-sm"
              : "text-on-surface-variant hover:text-primary-container hover:bg-surface-container/50 hover:translate-x-1"
              }`}
          >
            <span className="material-symbols-outlined text-xl">trending_up</span>
            <span className="font-label-md text-label-md line-clamp-2">
              Vínculos Populares
            </span>
          </button>

          <div className="border-t border-outline-variant my-3" />

          <div className="px-2 mb-2">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary block">
              Secciones
            </span>
          </div>

          <nav className="min-h-0 flex-1 flex flex-col gap-1 overflow-y-auto pr-2 pb-4">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <CircularProgress size={30} />
              </div>
            ) : (
              secciones.map((seccion) => (
                <button
                  key={seccion.id_seccion}
                  onClick={() => {
                    context?.setActiveView('seccion');
                    context?.setActiveSeccionId(seccion.id_seccion);
                    if (location.pathname.includes("admin")) {
                      navigate(`/admin/vinculos/${seccion.id_seccion}`);
                    } else {
                      navigate("/home/" + hash);
                    }
                    closeMobileAside();
                  }}
                  className={`flex items-center gap-3 px-3 py-3 rounded-3xl text-left transition-all duration-200 ${context?.activeView === 'seccion' && activeSeccionId === seccion.id_seccion
                    ? "bg-surface-container-highest text-primary font-bold shadow-sm"
                    : "text-on-surface-variant hover:text-primary-container hover:bg-surface-container/50 hover:translate-x-1"
                    }`}
                >
                  <span className="material-symbols-outlined text-xl" style={seccion.destacada === 1 ? { fontVariationSettings: "'FILL' 1" } : {}}>
                    {seccion.destacada === 1 ? "star" : "folder"}
                  </span>
                  <span className="font-label-md text-label-md line-clamp-2">
                    {seccion.seccion}
                  </span>
                </button>
              ))
            )}
          </nav>

          {/* <div className="mt-auto pt-4 border-t border-outline-variant">
            <a
              href="http://blog.colpegasus.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-primary text-white py-3 px-4 font-label-md text-label-md hover:bg-primary-container transition-colors active:scale-95 duration-100 rounded-3xl flex items-center justify-center gap-2 no-underline"
            >
              <span className="material-symbols-outlined text-sm">open_in_new</span>
              Ver Documentación
            </a>
          </div> */}
        </aside>
      </div>
    </>
  );
};

export default Aside;
